import React, { useEffect, useState } from 'react';
import CircularProgress from '../../ui/circular_progress';
import ChecklistItem from '../../ui/checklist_item';
import Typography from '../../ui/typography';
import MatchedMentorCard from './matched_card';
import styles from './index.module.css';

const HeartIcon = () => (
  <svg viewBox='0 0 16 16' width='14' height='14' aria-hidden='true'>
    <path d='M8 13.5s-4.7-3-6.3-6A3.6 3.6 0 0 1 8 3.5a3.6 3.6 0 0 1 6.3 4c-1.6 3-6.3 6-6.3 6z' fill='currentColor' />
  </svg>
);

const SparkleIcon = ({ size = 16 }) => (
  <svg viewBox='0 0 16 16' width={size} height={size} aria-hidden='true'>
    <path d='M8 1l1.6 4.4L14 7l-4.4 1.6L8 13l-1.6-4.4L2 7l4.4-1.6z' fill='currentColor' />
    <path d='M13 11l.6 1.4L15 13l-1.4.6L13 15l-.6-1.4L11 13l1.4-.6z' fill='currentColor' opacity='0.7' />
  </svg>
);

const CheckBoxedIcon = () => (
  <svg viewBox='0 0 16 16' width='14' height='14' aria-hidden='true'>
    <path
      d='M3.5 8.5l3 3 6-6.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const DEFAULT_STEPS = [
  { id: 'condition', label: 'Reviewing your responses', icon: <HeartIcon /> },
  { id: 'stage', label: 'Considering your preferences', icon: <SparkleIcon /> },
  { id: 'values', label: 'Preparing your mentor match', icon: <CheckBoxedIcon /> },
];

const stepStateAt = (index, activeIndex) => {
  if (index < activeIndex) return 'done';
  if (index === activeIndex) return 'active';
  return 'pending';
};

const SignupMentorMatch = ({
  view: controlledView,
  progress: controlledProgress,
  activeStepIndex: controlledStepIndex,
  steps = DEFAULT_STEPS,
  mentor,
  searchHeading = 'Finding the right mentor for you',
  matchedTitle = "You've been matched",
  continueLabel,
  onContinue,
  onClose,
  autoplay = false,
  autoplayDurationMs = 4500,
  revealDelayMs = 400,
}) => {
  const isControlled = controlledView !== undefined;

  const [autoView, setAutoView] = useState('searching');
  const [autoProgress, setAutoProgress] = useState(0);
  const [autoStep, setAutoStep] = useState(0);

  useEffect(() => {
    if (!autoplay || isControlled) return undefined;

    const targetPct = Math.max(1, Math.round(mentor?.matchPercent ?? 84));
    const tickMs = 60;
    const totalTicks = Math.max(8, Math.round(autoplayDurationMs / tickMs));
    const stepBoundary = Math.max(1, Math.floor(totalTicks / steps.length));
    let tick = 0;
    let revealTimeoutId = null;

    const id = setInterval(() => {
      tick += 1;
      const pct = Math.min(targetPct, Math.round((tick / totalTicks) * targetPct));
      setAutoProgress(pct);
      const nextStep = Math.min(steps.length - 1, Math.floor(tick / stepBoundary));
      setAutoStep(nextStep);
      if (tick >= totalTicks) {
        clearInterval(id);
        setAutoStep(steps.length);
        revealTimeoutId = setTimeout(() => setAutoView('matched'), revealDelayMs);
      }
    }, tickMs);

    return () => {
      clearInterval(id);
      if (revealTimeoutId) clearTimeout(revealTimeoutId);
    };
  }, [autoplay, isControlled, autoplayDurationMs, revealDelayMs, steps.length, mentor?.matchPercent]);

  const view = isControlled ? controlledView : autoView;
  const progress = controlledProgress ?? autoProgress;
  const activeStepIndex = controlledStepIndex ?? autoStep;
  const isMatched = view === 'matched';

  return (
    <div className={styles.root} data-view={view}>
      <div className={styles.backdrop} aria-hidden='true'>
        <div className={styles.bgApp}>
          <aside className={styles.bgRail}>
            <span className={styles.bgLogo} />
            <div className={styles.bgRailNav}>
              <span className={styles.bgRailItemActive} />
              <span className={styles.bgRailItem} />
              <span className={styles.bgRailItem} />
            </div>
            <span className={styles.bgRailAvatar} />
          </aside>

          <section className={styles.bgList}>
            <div className={styles.bgListHeader}>
              <span className={styles.bgListTitle} />
              <span className={styles.bgListBell} />
            </div>
            <div className={styles.bgListRowActive}>
              <span className={styles.bgAvatar} />
              <div className={styles.bgStack}>
                <span className={styles.bgLineStrong} />
                <span className={styles.bgLineFaint} />
              </div>
            </div>
          </section>

          <section className={styles.bgChat}>
            <div className={styles.bgChatHeader}>
              <span className={styles.bgAvatarSm} />
              <div className={styles.bgStack}>
                <span className={styles.bgLineStrong} />
                <span className={styles.bgLineFaint} />
              </div>
              <span className={styles.bgHeaderBtn} />
              <span className={styles.bgHeaderBtn} />
            </div>
            <div className={styles.bgChatBody}>
              <div className={styles.bgIntro}>
                <span className={styles.bgIntroEyebrow} />
                <span className={styles.bgIntroLine} />
                <span className={styles.bgIntroLine} />
                <span className={styles.bgIntroLine} />
                <span className={styles.bgIntroLineShort} />
              </div>
            </div>
            <div className={styles.bgComposer}>
              <span className={styles.bgComposerDot} />
              <span className={styles.bgComposerInput} />
              <span className={styles.bgComposerSend} />
            </div>
          </section>

          <section className={styles.bgDetail}>
            <div className={styles.bgHero}>
              <span className={styles.bgHeroScrim} />
              <span className={styles.bgHeroLineStrong} />
              <span className={styles.bgHeroLineFaint} />
            </div>
            <div className={styles.bgDetailBody}>
              <span className={styles.bgLineFaint} />
              <span className={styles.bgLineFaint} />
              <span className={styles.bgLineShort} />
            </div>
            <span className={styles.bgCallBtn} />
            <span className={styles.bgCallBtnGhost} />
          </section>
        </div>
      </div>
      <div className={styles.stage}>
        <div className={`${styles.panel} ${isMatched ? styles.panelOut : styles.panelIn}`}>
          {!isMatched && (
            <div className={styles.searchPanel}>
              <CircularProgress value={progress} size={140} strokeWidth={10} label='Match' />
              <Typography variant='heading2xl' as='h1' align='center' className={styles.heading}>
                {searchHeading}
              </Typography>
              <ul className={styles.checklist}>
                {steps.map((step, i) => (
                  <ChecklistItem key={step.id || i} state={stepStateAt(i, activeStepIndex)} icon={step.icon}>
                    {step.label}
                  </ChecklistItem>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className={`${styles.panel} ${isMatched ? styles.panelIn : styles.panelOut}`} aria-hidden={!isMatched}>
          {isMatched && mentor && (
            <MatchedMentorCard
              mentor={mentor}
              matchedTitle={matchedTitle}
              continueLabel={continueLabel}
              onContinue={onContinue}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupMentorMatch;
